import { ApplicationEvent } from '@shared/infrastructure/app-event-bus';
import HubspotMeta from '@shared/providers/hubspot/hubspot-meta';
import { ProviderMeta } from '@shared/providers/provider-meta';
import { CancelToken, DataField, DataObject } from '@shared/providers/types';
import { Flowstep, ProviderType } from '@shared/types/flowstep';
import Highland from 'highland';
import { DeleteResult, InsertResult, PullResult, PushObjectRecord, UpdateResult } from './provider-transport';

void Highland;

export class ProviderRefreshTokenError extends Error {
  constructor(message = 'Failed to refresh token') {
    super(message);
  }
}

export enum ProviderEvents {
  REMOTE_API_CHUNK = 'remote_api_chunk',
}

export class ProviderRemoteChunkEvent implements ApplicationEvent {
  type = ProviderEvents.REMOTE_API_CHUNK;
  constructor(public readonly chunkSize: number) {}
}

export interface ProviderClient {
  getFlowstepData(flowstep: Flowstep, cancelToken?: CancelToken): Promise<Highland.Stream<PullResult>>;
  insertFlowstepData(
    flowstep: Flowstep,
    stream: Highland.Stream<PushObjectRecord>,
    cancelToken?: CancelToken,
  ): Promise<Highland.Stream<InsertResult>>;
  updateFlowstepData(
    flowstep: Flowstep,
    stream: Highland.Stream<PushObjectRecord>,
    cancelToken?: CancelToken,
  ): Promise<Highland.Stream<UpdateResult>>;
  deleteFlowstepData(
    flowstep: Flowstep,
    stream: Highland.Stream<PushObjectRecord>,
    cancelToken?: CancelToken,
  ): Promise<Highland.Stream<DeleteResult>>;
  loadObjectFields(objectType: string): Promise<DataField[]>;
  loadObjectTypes(): Promise<DataObject[]>;
  buildUrl(requestData: { flowstep?: Flowstep; objectId?: string }): Promise<string>;
  on?: (eventName: string, callback: (eventData: any) => void) => void;
  estimateDataSize?(fields: DataField[]): number;
}

export abstract class ProviderFactory {
  abstract getProviderClient(providerType: ProviderType, context?: Record<string, any>): ProviderClient;

  providersMeta: { [K in ProviderType]: ProviderMeta | null } = {
    [ProviderType.SF]: null,
    [ProviderType.HS]: new HubspotMeta(),
    [ProviderType.HubspotList]: new HubspotMeta(),
    [ProviderType.CSV]: null,
  };

  getProviderMeta(provider: ProviderType): ProviderMeta {
    const meta = this.providersMeta[provider];
    if (!meta) {
      throw new Error(`No Meta found for provider: ${provider}`);
    }
    return meta;
  }
}
